import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import { myStyles as styles } from '../libs/util';
import { CustomSnackbar } from './SnackbarComponent';
import { subscriptionURL } from '../libs/firebase';
import { DesignFirmType } from 'ts-interfaces';
import { firebaseHelper, useDesignFirm } from 'ui';

export const Settings = ({ designFirmID }) => {
    const [boardsPrivateByDefault, setBoardsPrivateByDefault] = useState(true);
    const [designerCanMakePublic, setDesignerCanMakePublic] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const { firm, saveSettings } = useDesignFirm(firebaseHelper.db, designFirmID, subscriptionURL) as {
        firm: DesignFirmType;
        saveSettings: (settings: any) => Promise<{success: boolean, error?: any}>;
        savingSettings: boolean;
    };

    const handlePrivateByDefaultChange = async (e) => {
        const newValue = e.target.value === 'true';
        setBoardsPrivateByDefault(newValue);
        
        // Save to backend/database
        await handleSaveSettings({
            boardsPrivateByDefault: newValue,
            designerCanMakePublic
        });
        
        // Show feedback
        setSnackbarMessage(`Boards will now be ${newValue ? 'private' : 'public'} by default`);
        setSnackbarOpen(true);
    };

    // Handler for "Designer Can Make Boards Public" setting
    const handleDesignerPermissionChange = async (e) => {
        const newValue = e.target.value === 'true';
        setDesignerCanMakePublic(newValue);
        
        // Save to backend/database
        await handleSaveSettings({
            boardsPrivateByDefault,
            designerCanMakePublic: newValue
        });
        
        // Show feedback
        setSnackbarMessage(`Designers can ${newValue ? 'now' : 'no longer'} make boards public`);
        setSnackbarOpen(true);
    };

    // Close snackbar
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSaveSettings = async (settings) => {
        console.log(`Saving settings for design firm ${designFirmID}:`, settings);
        const result = await saveSettings(settings);
        if (result.success) {
            return;
        } else {
            console.error("Failed to save settings:", result.error);
        }
    };

    // Load settings on component mount (if needed)
    useEffect(() => {
        if (firm && firm.boardsPrivateByDefault !== undefined && firm.designerCanMakePublic !== undefined) {
            setBoardsPrivateByDefault(firm.boardsPrivateByDefault);
            setDesignerCanMakePublic(firm.designerCanMakePublic);
        }
    }, [firm]);

    return (
        <>
            <Box sx={styles.container}>
                <Typography variant="h4" component="h1" fontWeight="500" gutterBottom>
                    Settings
                </Typography>
                
                <Paper elevation={2} sx={{ mt: 3, mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 2 }}>
                        <Typography variant="h6">
                            Board Privacy Settings
                        </Typography>
                    </Box>
                    
                    <Box sx={{ p: 3 }}>
                        <Stack spacing={4}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <LockIcon color="primary" />
                                        <Box flexGrow={1}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                    Boards are Private By Default
                                                </FormLabel>
                                                <RadioGroup
                                                    row
                                                    name="privateByDefault"
                                                    value={boardsPrivateByDefault.toString()}
                                                    onChange={handlePrivateByDefaultChange}
                                                >
                                                    <FormControlLabel 
                                                        value="true" 
                                                        control={<Radio />} 
                                                        label="Yes" 
                                                    />
                                                    <FormControlLabel 
                                                        value="false" 
                                                        control={<Radio />} 
                                                        label="No" 
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                When enabled, all new boards will be private by default.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>
                                    <Box display="flex" alignItems="flex-start" gap={2}>
                                        <SecurityIcon color="primary" />
                                        <Box flexGrow={1}>
                                            <FormControl component="fieldset">
                                                <FormLabel component="legend" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                    Designer Can Make Boards Public
                                                </FormLabel>
                                                <RadioGroup
                                                    row
                                                    name="designerPermission"
                                                    value={designerCanMakePublic.toString()}
                                                    onChange={handleDesignerPermissionChange}
                                                >
                                                    <FormControlLabel 
                                                        value="true" 
                                                        control={<Radio />} 
                                                        label="Yes" 
                                                    />
                                                    <FormControlLabel 
                                                        value="false" 
                                                        control={<Radio />} 
                                                        label="No" 
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                When enabled, designers will be able to make their boards public.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Box>
                </Paper>
            </Box>
            <CustomSnackbar
                open={snackbarOpen}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
            />
        </>
    );
};